import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  PlusIcon, 
  SearchIcon, 
  FilterIcon, 
  TrendingUpIcon, 
  ClockIcon, 
  CheckCircleIcon, 
  AlertTriangleIcon,
  UserIcon,
  BuildingIcon,
  ActivityIcon,
  BarChart3Icon
} from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { getApiEndpoint } from "@/lib/api";
import { getToken, getUser } from "@/lib/auth-storage";
import { useLanguage } from "@/contexts/LanguageContext";

interface Ticket {
  id: string;
  parent_id?: string | null;
  title: string;
  description: string;
  type: string;
  category: string;
  subcategory?: string;
  priority: string;
  status: string;
  impact: string;
  urgency: string;
  queue_level?: string;
  transfer_count?: number;
  requester: { id: number; name: string; email: string } | null;
  assignee: { id: number; name: string; email: string } | null;
  company: { id: number; name: string; domain: string } | null;
  sla_remaining: string;
  sla_expired: boolean;
  created_at: string;
  updated_at: string;
  linked_count?: number; // Quantidade de chamados vinculados (filhos)
}

const InternalDashboard = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("active");
  const [queueFilter, setQueueFilter] = useState("all");
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [companyFilter, setCompanyFilter] = useState("all");
  const [assigneeFilter, setAssigneeFilter] = useState("all");
  const [slaFilter, setSlaFilter] = useState("all");
  const [user, setUser] = useState<any>(null);
  const [dashboardStats, setDashboardStats] = useState<any>(null);

  useEffect(() => {
    const checkUserAndFetch = () => {
      const userData = getUser();
      if (!userData) {
        navigate("/login");
        return;
      }

      try {
        const parsedUser = JSON.parse(userData);
        setUser(parsedUser);

        // Verificar se é usuário interno
        if (parsedUser.role !== "internal" && parsedUser.role !== "analyst" && parsedUser.role !== "admin") {
          navigate("/chamados");
          return;
        }

        fetchTickets();
        fetchDashboardStats();
      } catch (error) {
        console.error("Erro ao parsear usuário:", error);
      }
    };

    checkUserAndFetch();

    // Listener para mudanças de usuário
    const handleUserChange = () => {
      checkUserAndFetch();
    };

    window.addEventListener("userChanged", handleUserChange);

    return () => {
      window.removeEventListener("userChanged", handleUserChange);
    };
  }, [navigate]);

  const fetchDashboardStats = async () => {
    try {
      const token = getToken();
      const response = await fetch(getApiEndpoint("/api/tickets/stats/dashboard"), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setDashboardStats(data);
      }
    } catch (error) {
      console.error("Erro ao buscar estatísticas:", error);
    }
  };

  const fetchTickets = async () => {
    try {
      setLoading(true);
      const token = getToken();
      
      const response = await fetch(getApiEndpoint("/api/tickets"), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        console.log('📥 TICKETS RECEBIDOS:', data.tickets?.length, 'tickets');
        if (data.tickets?.[0]) {
          console.log('   Exemplo - Ticket:', data.tickets[0].id);
          console.log('   queue_level:', data.tickets[0].queue_level);
          console.log('   transfer_count:', data.tickets[0].transfer_count);
        }
        setTickets(data.tickets || []);
      } else {
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const filteredTickets = tickets.filter(ticket => {
    const matchesSearch = ticket.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         ticket.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         ticket.requester?.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         ticket.company?.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === "all" ||
      (statusFilter === "active" && ["open", "in_progress", "waiting_response"].includes(ticket.status)) ||
      ticket.status === statusFilter;
    const matchesPriority = priorityFilter === "all" || ticket.priority === priorityFilter;
    const matchesType = typeFilter === "all" || ticket.type === typeFilter;
    const matchesQueue = queueFilter === "all" || ticket.queue_level === queueFilter;
    const matchesCompany = companyFilter === "all" || ticket.company?.id.toString() === companyFilter;
    const matchesAssignee = assigneeFilter === "all" || 
      (assigneeFilter === "unassigned" && !ticket.assignee) ||
      (ticket.assignee && ticket.assignee.id.toString() === assigneeFilter);
    const matchesSLA = slaFilter === "all" || 
      (slaFilter === "expired" && ticket.sla_expired) ||
      (slaFilter === "warning" && !ticket.sla_expired && ticket.sla_remaining.includes("h") && parseInt(ticket.sla_remaining) < 2);
    
    return matchesSearch && matchesStatus && matchesPriority && matchesType && matchesQueue && 
           matchesCompany && matchesAssignee && matchesSLA;
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case "open": return "bg-blue-100 text-blue-800";
      case "in_progress": return "bg-yellow-100 text-yellow-800";
      case "waiting_response": return "bg-orange-100 text-orange-800";
      case "resolved": return "bg-green-100 text-green-800";
      case "closed": return "bg-gray-100 text-gray-800";
      default: return "bg-gray-100 text-gray-800";
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case "open": return t.dashboard.statusOpen;
      case "in_progress": return t.dashboard.statusInProgress;
      case "waiting_response": return t.dashboard.statusWaiting;
      case "resolved": return t.dashboard.statusResolved;
      case "closed": return t.dashboard.statusClosed;
      default: return status;
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case "critical": return "bg-red-100 text-red-800";
      case "high": return "bg-orange-100 text-orange-800";
      case "medium": return "bg-yellow-100 text-yellow-800";
      case "low": return "bg-green-100 text-green-800";
      default: return "bg-gray-100 text-gray-800";
    }
  };

  const getPriorityLabel = (priority: string) => {
    switch (priority) {
      case "critical": return t.dashboard.priorityCritical;
      case "high": return t.dashboard.priorityHigh;
      case "medium": return t.dashboard.priorityMedium;
      case "low": return t.dashboard.priorityLow;
      default: return priority;
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case "incident": return <AlertTriangleIcon className="h-4 w-4 text-red-600" />;
      case "request": return <CheckCircleIcon className="h-4 w-4 text-blue-600" />;
      case "problem": return <TrendingUpIcon className="h-4 w-4 text-purple-600" />;
      case "change": return <ClockIcon className="h-4 w-4 text-green-600" />;
      default: return <ClockIcon className="h-4 w-4 text-gray-600" />;
    }
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case "incident": return t.dashboard.typeIncident;
      case "request": return t.dashboard.typeRequest;
      case "problem": return t.dashboard.typeProblem;
      case "change": return t.dashboard.typeChange;
      default: return type;
    }
  };

  const formatDateTime = (value?: string | null) => {
    if (!value) return "";
    return new Date(value).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' });
  };

  // Estatísticas
  const stats = {
    total: tickets.length,
    open: tickets.filter(t => t.status === "open").length,
    inProgress: tickets.filter(t => t.status === "in_progress").length,
    resolved: tickets.filter(t => t.status === "resolved").length,
    slaExpired: tickets.filter(t => t.sla_expired).length,
    unassigned: tickets.filter(t => !t.assignee).length,
    critical: tickets.filter(t => t.priority === "critical").length,
    byCategory: dashboardStats?.by_category || {},
    byTechnician: dashboardStats?.by_technician || {},
    npsAverage: dashboardStats?.nps_average || null,
    reopenRate: dashboardStats?.reopen_rate || 0,
    reopenCount: dashboardStats?.reopen_count || 0
  };

  // Empresas únicas para filtro
  const uniqueCompanies = Array.from(
    new Map(tickets.map(t => t.company).filter(Boolean).map(c => [c!.id, c])).values()
  );

  // Usuários internos únicos para filtro
  const uniqueAssignees = Array.from(
    new Map(tickets.map(t => t.assignee).filter(Boolean).map(a => [a!.id, a])).values()
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">{t.dashboard.loading}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <Header />
      
      <main className="container mx-auto px-4 py-8">
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">{t.dashboard.title}</h1>
            <p className="text-gray-600 dark:text-gray-400">{t.dashboard.subtitle}</p>
          </div>
          <div className="flex gap-2 mt-4 lg:mt-0">
            <Button variant="outline" onClick={fetchTickets}>
              <ActivityIcon className="h-4 w-4 mr-2" />
              {t.dashboard.refresh}
            </Button>
          </div>
        </div>

        {/* KPIs Expandidos */}
        <div className="grid grid-cols-2 lg:grid-cols-7 gap-4 mb-8">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-xs font-medium text-gray-600">{t.dashboard.total}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-gray-900">{stats.total}</div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-xs font-medium text-gray-600">{t.dashboard.open}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-blue-600">{stats.open}</div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-xs font-medium text-gray-600">{t.dashboard.inProgress}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-yellow-600">{stats.inProgress}</div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-xs font-medium text-gray-600">{t.dashboard.resolved}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">{stats.resolved}</div>
            </CardContent>
          </Card>

          <Card className="border-red-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-xs font-medium text-red-600">{t.dashboard.slaExpired}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-red-600">{stats.slaExpired}</div>
            </CardContent>
          </Card>

          <Card className="border-orange-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-xs font-medium text-orange-600">{t.dashboard.unassigned}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-orange-600">{stats.unassigned}</div>
            </CardContent>
          </Card>

          <Card className="border-red-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-xs font-medium text-red-600">{t.dashboard.critical}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-red-600">{stats.critical}</div>
            </CardContent>
          </Card>
        </div>

        {/* Métricas de Filas de Atendimento */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <Card className="border-green-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-green-700 flex items-center">
                🟢 {t.dashboard.queueN1}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-green-600">
                {tickets.filter(t => t.queue_level === 'N1' && (t.status === 'open' || t.status === 'in_progress')).length}
              </div>
              <p className="text-xs text-gray-500 mt-1">{t.dashboard.activeTickets}</p>
            </CardContent>
          </Card>

          <Card className="border-yellow-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-yellow-700 flex items-center">
                🟡 {t.dashboard.queueN2}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-yellow-600">
                {tickets.filter(t => t.queue_level === 'N2' && (t.status === 'open' || t.status === 'in_progress')).length}
              </div>
              <p className="text-xs text-gray-500 mt-1">{t.dashboard.activeTickets}</p>
            </CardContent>
          </Card>

          <Card className="border-red-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-red-700 flex items-center">
                🔴 {t.dashboard.queueN3}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-red-600">
                {tickets.filter(t => t.queue_level === 'N3' && (t.status === 'open' || t.status === 'in_progress')).length}
              </div>
              <p className="text-xs text-gray-500 mt-1">{t.dashboard.activeTickets}</p>
            </CardContent>
          </Card>
        </div>

        {/* Estatísticas Detalhadas */}
        {dashboardStats && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            {/* Chamados por Categoria */}
            <Card>
              <CardHeader>
                <CardTitle>{t.dashboard.byCategory}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {Object.entries(stats.byCategory).map(([category, count]: [string, any]) => (
                    <div key={category} className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">{category}</span>
                      <Badge variant="outline">{count}</Badge>
                    </div>
                  ))}
                  {Object.keys(stats.byCategory).length === 0 && (
                    <p className="text-sm text-gray-500 text-center py-4">{t.dashboard.noData}</p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Chamados por Técnico */}
            <Card>
              <CardHeader>
                <CardTitle>{t.dashboard.byTechnician}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {Object.entries(stats.byTechnician).map(([technician, count]: [string, any]) => (
                    <div key={technician} className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">{technician}</span>
                      <Badge variant="outline">{count}</Badge>
                    </div>
                  ))}
                  {Object.keys(stats.byTechnician).length === 0 && (
                    <p className="text-sm text-gray-500 text-center py-4">{t.dashboard.noData}</p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* NPS e Reabertura */}
            <Card>
              <CardHeader>
                <CardTitle>{t.dashboard.qualityMetrics}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="text-sm text-gray-600 mb-1">{t.dashboard.npsAverage}</p>
                  <p className="text-2xl font-bold text-blue-600">
                    {stats.npsAverage !== null ? stats.npsAverage.toFixed(1) : 'N/A'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1">{t.dashboard.reopenRate}</p>
                  <p className="text-2xl font-bold text-orange-600">
                    {stats.reopenRate}%
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {t.dashboard.reopenedCount(stats.reopenCount)}
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Métricas de Transferências */}
            <Card>
              <CardHeader>
                <CardTitle>{t.dashboard.transfersEscalations}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="text-sm text-gray-600 mb-1">{t.dashboard.totalTransfers}</p>
                  <p className="text-2xl font-bold text-purple-600">
                    {tickets.reduce((sum, t) => sum + (t.transfer_count || 0), 0)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1">{t.dashboard.ticketsTransferred}</p>
                  <p className="text-2xl font-bold text-purple-600">
                    {tickets.filter(t => (t.transfer_count || 0) > 0).length}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {t.dashboard.multipleTransfers(tickets.filter(t => (t.transfer_count || 0) > 1).length)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1">{t.dashboard.avgPerTicket}</p>
                  <p className="text-2xl font-bold text-purple-600">
                    {tickets.length > 0 
                      ? (tickets.reduce((sum, t) => sum + (t.transfer_count || 0), 0) / tickets.length).toFixed(1)
                      : '0'}
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filtros Avançados */}
        <Card className="mb-8 dark:bg-gray-800 dark:border-gray-700">
          <CardHeader>
            <CardTitle className="flex items-center dark:text-white">
              <FilterIcon className="h-5 w-5 mr-2" />
              {t.dashboard.filtersTitle}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7 gap-4">
              <div className="relative">
                <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder={t.dashboard.searchPlaceholder}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger>
                  <SelectValue placeholder={t.dashboard.status} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">{t.dashboard.statusActive}</SelectItem>
                  <SelectItem value="all">{t.dashboard.statusAll}</SelectItem>
                  <SelectItem value="open">{t.dashboard.statusOpen}</SelectItem>
                  <SelectItem value="in_progress">{t.dashboard.statusInProgress}</SelectItem>
                  <SelectItem value="waiting_response">{t.dashboard.statusWaiting}</SelectItem>
                  <SelectItem value="resolved">{t.dashboard.statusResolved}</SelectItem>
                  <SelectItem value="closed">{t.dashboard.statusClosed}</SelectItem>
                </SelectContent>
              </Select>
              
              <Select value={priorityFilter} onValueChange={setPriorityFilter}>
                <SelectTrigger>
                  <SelectValue placeholder={t.dashboard.priority} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t.dashboard.priorityAll}</SelectItem>
                  <SelectItem value="critical">{t.dashboard.priorityCritical}</SelectItem>
                  <SelectItem value="high">{t.dashboard.priorityHigh}</SelectItem>
                  <SelectItem value="medium">{t.dashboard.priorityMedium}</SelectItem>
                  <SelectItem value="low">{t.dashboard.priorityLow}</SelectItem>
                </SelectContent>
              </Select>

              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger>
                  <SelectValue placeholder={t.dashboard.type} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t.dashboard.typeAll}</SelectItem>
                  <SelectItem value="incident">{t.dashboard.typeIncident}</SelectItem>
                  <SelectItem value="request">{t.dashboard.typeRequest}</SelectItem>
                  <SelectItem value="problem">{t.dashboard.typeProblem}</SelectItem>
                  <SelectItem value="change">{t.dashboard.typeChange}</SelectItem>
                </SelectContent>
              </Select>

              <Select value={queueFilter} onValueChange={setQueueFilter}>
                <SelectTrigger>
                  <SelectValue placeholder={t.dashboard.queue} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t.dashboard.queueAll}</SelectItem>
                  <SelectItem value="N1">🟢 {t.dashboard.queueN1Short}</SelectItem>
                  <SelectItem value="N2">🟡 {t.dashboard.queueN2Short}</SelectItem>
                  <SelectItem value="N3">🔴 {t.dashboard.queueN3Short}</SelectItem>
                </SelectContent>
              </Select>

              <Select value={companyFilter} onValueChange={setCompanyFilter}>
                <SelectTrigger>
                  <SelectValue placeholder={t.dashboard.company} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t.dashboard.companyAll}</SelectItem>
                  {uniqueCompanies.map(company => (
                    <SelectItem key={company.id} value={company.id.toString()}>
                      {company.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={assigneeFilter} onValueChange={setAssigneeFilter}>
                <SelectTrigger>
                  <SelectValue placeholder={t.dashboard.assignee} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t.dashboard.assigneeAll}</SelectItem>
                  <SelectItem value="unassigned">{t.dashboard.assigneeUnassigned}</SelectItem>
                  {uniqueAssignees.map(assignee => (
                    <SelectItem key={assignee.id} value={assignee.id.toString()}>
                      {assignee.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={slaFilter} onValueChange={setSlaFilter}>
                <SelectTrigger>
                  <SelectValue placeholder={t.dashboard.sla} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t.dashboard.slaAll}</SelectItem>
                  <SelectItem value="expired">{t.dashboard.slaExpiredFilter}</SelectItem>
                  <SelectItem value="warning">{t.dashboard.slaWarning}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Button 
              variant="outline" 
              className="mt-4"
              onClick={() => {
                setSearchTerm("");
                setStatusFilter("all");
                setPriorityFilter("all");
                setTypeFilter("all");
                setQueueFilter("all");
                setCompanyFilter("all");
                setAssigneeFilter("all");
                setSlaFilter("all");
              }}
            >
              {t.dashboard.clearFilters}
            </Button>
          </CardContent>
        </Card>

        {/* Lista de Chamados */}
        <Card className="dark:bg-gray-800 dark:border-gray-700">
          <CardHeader>
            <CardTitle className="dark:text-white">{t.dashboard.ticketsList} ({filteredTickets.length})</CardTitle>
            <CardDescription className="dark:text-gray-400">
              {t.dashboard.ticketsListDesc}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {filteredTickets.map((ticket) => (
                <Link key={ticket.id} to={`/atendimento/chamado/${ticket.id}`}>
                  <div className={`rounded-lg p-4 border-2 cursor-pointer transition-colors ${
                    ticket.sla_expired 
                      ? 'border-red-600 bg-red-50 dark:border-red-900/70 dark:bg-red-950/20' 
                      : !ticket.assignee 
                      ? 'border-orange-300 bg-orange-50 dark:border-orange-800/60 dark:bg-orange-950/20' 
                      : 'border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800'
                  }`}>
                    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2 flex-wrap">
                          {getTypeIcon(ticket.type)}
                          <span className="font-semibold text-gray-900 dark:text-white">#{ticket.id}</span>
                          <Badge className={getStatusColor(ticket.status)}>
                            {getStatusLabel(ticket.status)}
                          </Badge>
                          <Badge className={getPriorityColor(ticket.priority)}>
                            {getPriorityLabel(ticket.priority)}
                          </Badge>
                          <Badge className={
                            ticket.queue_level === 'N3' ? 'bg-red-100 text-red-800 border-red-300' :
                            ticket.queue_level === 'N2' ? 'bg-yellow-100 text-yellow-800 border-yellow-300' :
                            'bg-green-100 text-green-800 border-green-300'
                          }>
                            {(() => {
                              const queue = ticket.queue_level || 'N1';
                              console.log(`Badge ${ticket.id}: queue_level="${ticket.queue_level}" final="${queue}"`);
                              return queue;
                            })()}
                          </Badge>
                          {ticket.transfer_count > 0 && (
                            <Badge className="bg-purple-100 text-purple-800 border-purple-300">
                              🔄 {ticket.transfer_count}
                            </Badge>
                          )}
                          {ticket.sla_expired && (
                            <Badge className="bg-red-100 text-red-800">
                              {t.dashboard.slaExpired}
                            </Badge>
                          )}
                          {!ticket.assignee && (
                            <Badge className="bg-orange-100 text-orange-800">
                              {t.dashboard.unassigned}
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <h3 className="font-medium text-gray-900 dark:text-white">{ticket.title}</h3>
                          {ticket.linked_count && ticket.linked_count > 0 && (
                            <Badge variant="secondary" className="bg-blue-100 text-blue-800 text-xs whitespace-nowrap">
                              {t.dashboard.linkedReported(ticket.linked_count)}
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-2 line-clamp-2">{ticket.description}</p>
                        <div className="flex flex-wrap gap-3 text-xs text-gray-500 dark:text-gray-400">
                          <span className="flex items-center gap-1">
                            <BuildingIcon className="h-3 w-3" />
                            {ticket.company?.name || t.dashboard.noCompany}
                          </span>
                          <span className="flex items-center gap-1">
                            <UserIcon className="h-3 w-3" />
                            {ticket.requester?.name || t.dashboard.noRequester}
                          </span>
                          <span>{t.dashboard.category}: {ticket.category}</span>
                          <span>•</span>
                          <span>{t.dashboard.created}: {formatDateTime(ticket.created_at)}</span>
                          <span>•</span>
                          <span className={ticket.sla_expired ? 'text-red-600 dark:text-red-400 font-semibold' : 'dark:text-gray-400'}>
                            SLA: {ticket.sla_remaining}
                          </span>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm text-gray-600 dark:text-gray-300 mb-1">{t.dashboard.assigneeLabel}</div>
                        <div className="font-medium text-gray-900 dark:text-white">
                          {ticket.assignee ? ticket.assignee.name : t.dashboard.notAssigned}
                        </div>
                        {ticket.assignee && (
                          <div className="text-xs text-gray-500 dark:text-gray-400">{ticket.assignee.email}</div>
                        )}
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
              
              {filteredTickets.length === 0 && (
                <div className="text-center py-12">
                  <BarChart3Icon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">{t.dashboard.noTicketsFound}</h3>
                  <p className="text-gray-600 dark:text-gray-400">{t.dashboard.adjustFilters}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </main>

      <Footer />
    </div>
  );
};

export default InternalDashboard;

